import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { oneLine } from 'common-tags';
import { Middleware } from 'redux';

import { defaultMiddlewareOptions } from './defaults';
import {
  AxiosActionError,
  AxiosActionResponse,
  AxiosClient,
  AxiosFlaggedAction,
  AxiosMultiClient,
  CustomMiddlewareOptions
} from './types';
import {
  getClientName,
  getFulfilledAction,
  getPendingAction,
  getRejectedAction,
  getRequestConfig,
  isAxiosRequest
} from './utils';

/**
 * Axios Middlware default client to be used to communicate with external resources.
 * @param {AxiosInstance} client
 * @param {Partial<CustomMiddlewareOptions>} customMiddlewareOptions
 * @returns {Middleware}
 */
export const axiosMiddlewareWrapper = (
  client: AxiosInstance,
  customMiddlewareOptions: Partial<CustomMiddlewareOptions> = {}
) => {
  const middlewareOptions: CustomMiddlewareOptions = { ...defaultMiddlewareOptions, ...customMiddlewareOptions };

  return axiosMiddlewareMultiClientWrapper({ [middlewareOptions.clientName]: { client } }, middlewareOptions);
};

/**
 * Axios Middlware multi-client.
 * @param {AxiosMultiClient} clients
 * @param {Partial<CustomMiddlewareOptions>} customMiddlewareOptions
 * @returns {Middleware}
 */
export const axiosMiddlewareMultiClientWrapper = (
  clients: AxiosMultiClient,
  customMiddlewareOptions: Partial<CustomMiddlewareOptions> = {}
) => {
  const middlewareOptions: CustomMiddlewareOptions = { ...defaultMiddlewareOptions, ...customMiddlewareOptions };
  const setupedClients: AxiosMultiClient = {};

  /**
   * Axios Middleware to execute async call to external resources.
   * @returns {any}
   */
  const axiosMiddleware: Middleware = ({ dispatch }) => next => action => {

    if (!isAxiosRequest(action)) {
      return next(action);
    }

    const apiAction: AxiosFlaggedAction = action; // Forces to be an AxiosFlaggedAction type
    const clientName: string = getClientName(apiAction) || middlewareOptions.clientName;

    if (!clients[clientName]) {
      throw new Error(
        oneLine`Client with name '${clientName}' has not been defined in the AxiosMiddleware
        or it has not been added to the 'customMiddlewareOptions'`
      );
    }

    if (!setupedClients[clientName]) {
      setupedClients[clientName] = clients[clientName];
    }

    const setupedClient: AxiosClient = setupedClients[clientName];

    /**
     * Pass the pending action to the next middleware:
     * This object describes the pending state of a promise and could include
     * any data (for optimistic updates) and/or meta from the original action.
     */
    next(getPendingAction(apiAction));

    const requestConfig: AxiosFlaggedAction['payload']['request'] = getRequestConfig(apiAction);

    return setupedClient.client.request(requestConfig).then(
      (response: AxiosResponse): AxiosActionResponse => {
        const nextAction: AxiosActionResponse = getFulfilledAction(apiAction, response);

        /** Send the fulfilled action to the init of the middleware chain */
        dispatch(nextAction);

        return nextAction;
      },
      (error: AxiosError): never => {
        const nextAction: AxiosActionError = getRejectedAction(apiAction, error);

        /** Send the rejected action to the init of the middleware chain */
        dispatch(nextAction);

        throw error;
      }
    );
  };

  return axiosMiddleware;
};
